import {Action, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {ContentType, PlayingState} from '../engine/playable/playable';
import {Qualities} from '../engine/playable/qualities';
import {
  AppendPlayableLog,
  Init,
  RequestApi,
  SeekTo,
  SeekToLive,
  SelectQuality,
  SetApiData,
  SetCurrentTime,
  SetLoading,
  SetPlayableStats,
  SetPlayingState,
  SetQualities,
  SetQuality,
  SetTotalTime,
  SetVideoType,
  SetVolume,
  ToggleMute,
  TogglePlay,
  ToggleUI
} from './player.actions';
import {UiToPlayableService} from '../engine/ui-to-playable.service';
import {ApiService} from '../engine/api/api.service';
import {first} from 'rxjs/operators';
import {SettingsStoreService} from '../player/settings-store.service';

const defaultPoster = 'https://goodgame.ru/images/channel-logo.jpg';
const clamp = (val, min, max) => val > max ? max : val < min ? min : val;

export class PlayerStateModel {
  src: string;
  autoplay: boolean;
  isPlaying: boolean;
  isStarted: boolean;
  volume: number;
  volumeBeforeMute: number;
  contentType: ContentType;
  isUIVisible: boolean;
  isSeekable: boolean;
  isLive: boolean;
  isEmbeded: boolean;
  timings: {
    currentTime: number;
    totalTime: number;
    loadedTime: number;
  };
  loading: number;
  apiData: any;
  qualities: string[];
  currentQuality: string;
  poster: string;
  stats: any;
  log: string[];
}

export const defaults: PlayerStateModel = {
  src: '',
  autoplay: false,
  isPlaying: false,
  isStarted: false,
  volume: 0,
  volumeBeforeMute: 0,
  contentType: ContentType.MP4,
  isUIVisible: false,
  isSeekable: false,
  isLive: false,
  isEmbeded: false,
  timings: {
    currentTime: 0,
    totalTime: 0,
    loadedTime: 0
  },
  loading: 0,
  apiData: null,
  qualities: [],
  currentQuality: Qualities.SOURCE,
  poster: defaultPoster,
  stats: {},
  log: []
};


@State<PlayerStateModel>({name: 'player', defaults})
@Injectable()
export class PlayerState {
  constructor(
    private proto: UiToPlayableService,
    private apiService: ApiService,
    private settingsStore: SettingsStoreService,
  ) {
    // console.log('creating state', PlayerState);
  }

  @Action(Init)
  async Init(state: StateContext<PlayerStateModel>, {payload}: Init) {
    // console.log('Init: ', payload);
    return state.patchState(payload);
  }

  @Action(SetPlayingState)
  async SetPlayingState(state: StateContext<PlayerStateModel>, {payload}: SetPlayingState) {
    const isPlaying = payload.state === PlayingState.PLAYING || payload.state === PlayingState.STARTED;
    const isStarted = payload.state === PlayingState.STARTED;

    return state.patchState({isPlaying, isStarted});
  }

  @Action(SetVolume)
  async SetVolume(state: StateContext<PlayerStateModel>, {payload}: SetVolume) {
    const value = clamp(payload.volume, 0, 100);
    state.patchState({volume: value});
    this.saveSettings(state);
  }

  @Action(ToggleMute)
  async ToggleMute(state: StateContext<PlayerStateModel>) {
    const cState = state.getState();

    return state.patchState({
      volumeBeforeMute: cState.volume > 0 ? cState.volume : 0,
      volume: cState.volume > 0 ? 0 : cState.volumeBeforeMute
    });
  }

  @Action(SetLoading)
  async SetLoading(state: StateContext<PlayerStateModel>, {payload}: SetLoading) {
    return state.patchState({loading: payload.loading});
  }

  @Action(SetCurrentTime)
  async SetCurrentTime(state: StateContext<PlayerStateModel>, {payload}: SetCurrentTime) {
    return state.patchState({
      timings: {
        ...state.getState().timings,
        currentTime: payload.value
      }
    });
  }

  @Action(SetVideoType)
  async SetVideoType(state: StateContext<PlayerStateModel>, {payload}: SetVideoType) {
    const seekable = !!payload.seekable;
    const live = !!payload.live;

    return state.patchState({
      isSeekable: seekable,
      isLive: live
    });
  }

  @Action(SetQualities)
  async SetQualities(state: StateContext<PlayerStateModel>, {payload}: SetQualities) {
    // console.log('SetQualities', {payload});
    return state.patchState({qualities: payload.qualities});
  }

  @Action(SetQuality)
  async SetQuality(state: StateContext<PlayerStateModel>, {payload}: SetQuality) {
    state.patchState({currentQuality: payload.quality});
    this.saveSettings(state);
  }

  @Action(SetApiData)
  async SetApiData(state: StateContext<PlayerStateModel>, {payload}: SetApiData) {
    const apiData = payload;
    apiData.streamkey = state.getState().src;

    return state.patchState({apiData});
  }

  @Action(ToggleUI)
  async ToggleUI(state: StateContext<PlayerStateModel>, {payload}: ToggleUI) {
    const visibility = payload.state;
    if (visibility === state.getState().isUIVisible) {
      return;
    }

    return state.patchState({isUIVisible: visibility});
  }

  @Action(SetTotalTime)
  async SetTotalTime(state: StateContext<PlayerStateModel>, {payload}: SetTotalTime) {
    return state.patchState({
      timings: {
        ...state.getState().timings,
        totalTime: payload.value
      }
    });
  }

  @Action(SetPlayableStats)
  async SetPlayableStats(state: StateContext<PlayerStateModel>, {payload}: SetPlayableStats) {
    const oldStats = state.getState().stats;

    return state.patchState({
      stats: {
        ...oldStats,
        ...payload
      }
    });
  }

  @Action(AppendPlayableLog)
  async AppendPlayableLog(state: StateContext<PlayerStateModel>, {payload}: AppendPlayableLog) {
    const oldLog = state.getState().log;

    return state.patchState({
      log: [
        ...oldLog,
        payload
      ]
    });
  }

  @Action([TogglePlay, SeekTo, SeekToLive, SelectQuality])
  async TogglePlay(state: StateContext<PlayerStateModel>, action) {
    const event = {
      type: action.__proto__.constructor.type, // This is bad!
      payload: action.payload
    };

    this.proto.emitUIEvent(event);
  }


  @Action(RequestApi)
  async RequestApi(state: StateContext<PlayerStateModel>, {payload}: RequestApi) {
    const streamKey = payload.streamkey;
    const data = await this.apiService.getOldApi(streamKey).pipe(first()).toPromise();

    return this.SetApiData(state, {payload: data});
  }


  // TODO: Actually save it
  private saveSettings(state: StateContext<PlayerStateModel>) {
    const cState = state.getState();
    this.settingsStore.saveSettings(cState);
  }

}
